import React, {
  useMemo,
  useState,
} from "react";

import {
  FaLink,
  FaSearch,
  FaUserCheck,
} from "react-icons/fa";

import {
  USER_ROLE_LABELS,
} from "./../data/acteurAdmin.constants";

import {
  normalize,
} from "../utils/acteursAdmin.utils";

const getUserLabel = (user) =>
  `${String(
    user?.nom || "",
  ).toUpperCase()} ${user?.prenom || ""}`.trim();

export default function UserLinkSelect({
  users = [],
  value = "",
  onChange,
  linkedUsers = [],
  disabled = false,
}) {
  const [query, setQuery] = useState("");

  const hasExistingLink =
    linkedUsers.length > 0;

  const visibleUsers = useMemo(() => {
    const needle = normalize(query);

    let filteredUsers = [...users];

    if (needle) {
      filteredUsers = filteredUsers.filter(
        (user) => {
          const searchableValue = [
            user?.nom,
            user?.prenom,
            user?.email,
            USER_ROLE_LABELS[user?.role_id],
          ]
            .filter(Boolean)
            .join(" ");

          return normalize(
            searchableValue,
          ).includes(needle);
        },
      );
    }

    filteredUsers.sort((userA, userB) =>
      getUserLabel(userA).localeCompare(
        getUserLabel(userB),
        "fr",
        {
          sensitivity: "base",
        },
      ),
    );

    /*
     * Si une recherche est active, on conserve
     * tout de même l'utilisateur sélectionné
     * dans les options.
     */
    if (
      value &&
      !filteredUsers.some(
        (user) =>
          String(user.id) === String(value),
      )
    ) {
      const selectedUser = users.find(
        (user) =>
          String(user.id) === String(value),
      );

      if (selectedUser) {
        filteredUsers.unshift(selectedUser);
      }
    }

    return filteredUsers;
  }, [query, users, value]);

  return (
    <section className="border-t border-slate-200 pt-4 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <FaLink className="text-indigo-500" />

        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
          Compte utilisateur associé
        </h4>
      </div>

      {hasExistingLink ? (
        <div className="mt-3 rounded-2xl bg-emerald-50 p-3 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/20">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
            <FaUserCheck />
            Acteur déjà lié
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {linkedUsers.map((user) => (
              <span
                key={user.id}
                className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:bg-slate-900 dark:text-emerald-300 dark:ring-emerald-500/20"
              >
                {getUserLabel(user)}

                {user.email
                  ? ` — ${user.email}`
                  : ""}
              </span>
            ))}
          </div>

          <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300/80">
            Les associations existantes sont
            conservées.
          </p>
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Facultatif : sélectionnez le compte
            qui recevra les convocations de cet
            acteur.
          </p>

          <div className="relative mt-3">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={query}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              disabled={disabled}
              placeholder="Rechercher par nom, prénom ou email..."
              className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          <select
            value={value}
            onChange={(event) =>
              onChange(event.target.value)
            }
            disabled={disabled}
            className="mt-3 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          >
            <option value="">
              — Aucun utilisateur —
            </option>

            {visibleUsers.map((user) => (
              <option
                key={user.id}
                value={String(user.id)}
              >
                {getUserLabel(user)}

                {user.email
                  ? ` — ${user.email}`
                  : ""}

                {USER_ROLE_LABELS[user.role_id]
                  ? ` — ${
                      USER_ROLE_LABELS[
                        user.role_id
                      ]
                    }`
                  : ""}
              </option>
            ))}
          </select>

          {query &&
          visibleUsers.length === 0 ? (
            <div className="mt-2 text-xs text-amber-700">
              Aucun utilisateur ne correspond à
              cette recherche.
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}